import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ToastContainer } from 'react-toastify';
import VenueConductorOrb from './VenueConductorOrb';
import VenueConductorPage from './VenueConductorPage';
import VenueConductorLaunchTour from './VenueConductorLaunchTour';
import { existingAiIntegrationPropType } from '../../../shared/ExistingAiPanel';
import { conductorTheme } from '../themeTokens';
import client from '../../../shared/api/client';
import {
  DOCK_DEFAULT_WIDTH,
  DOCK_MIN_WIDTH,
  clampDockWidth,
  getQuickChatFrame,
  loadDockState,
  loadOrbState,
  saveDockState
} from '../dockState';
import '../styles/venueConductorOrb.css';

const dockBodyStyles = (width, isResizing) => `
  body.conductor-dock-open {
    margin-right: ${width}px;
    transition: ${isResizing ? 'none' : 'margin-right 0.15s ease'};
    ${isResizing ? 'cursor: col-resize; user-select: none;' : ''}
  }
`;

const HUBSPOT_WIDGET_HIDDEN_STYLES = `
  body.conductor-modal-open #hubspot-messages-iframe-container,
  body.conductor-modal-open #hubspot-conversations-inline-parent,
  body.conductor-modal-open iframe[id="hubspot-conversations-inline-iframe"],
  body.conductor-modal-open iframe[id^="hubspot-messages-iframe"],
  body.conductor-modal-open iframe[id^="hubspot-conversations-iframe"] {
    display: none !important;
  }
`;

const getIsMobileLayout = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.innerWidth < 768;
};

const hideHubSpotWidget = () => {
  document.body.classList.add('conductor-modal-open');

  const widget = window.HubSpotConversations?.widget;
  if (widget) {
    widget.close?.();
    widget.remove?.();
    return () => {};
  }

  const readyCallback = () => {
    window.HubSpotConversations?.widget?.close?.();
    window.HubSpotConversations?.widget?.remove?.();
  };

  window.hsConversationsOnReady = [...(window.hsConversationsOnReady || []), readyCallback];

  return () => {
    if (!Array.isArray(window.hsConversationsOnReady)) {
      return;
    }

    window.hsConversationsOnReady = window.hsConversationsOnReady.filter((callback) => callback !== readyCallback);
  };
};

const restoreHubSpotWidget = () => {
  document.body.classList.remove('conductor-modal-open');

  const widget = window.HubSpotConversations?.widget;
  if (!widget) {
    return;
  }

  const status = widget.status?.();
  if (status?.loaded) {
    widget.refresh?.();
    return;
  }

  widget.load?.();
};

const VenueConductorModal = ({
  teamId = null,
  teamName = null,
  currentUserId = null,
  teamMembershipId = null,
  conductorWidgetEnabled = true,
  currentUserAvatarUrl = null,
  existingAiIntegration = null,
  conductorVoiceEnabled = false,
  helpCenterEnabled = false,
  conductorTextingEnabled = false,
  conductorCuesEnabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileLayout, setIsMobileLayout] = useState(getIsMobileLayout);
  const [visualViewportInsets, setVisualViewportInsets] = useState(null);
  const [isLaunchTourVisible, setIsLaunchTourVisible] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState(null);
  const [pendingVoice, setPendingVoice] = useState(false);
  const [presentationMode, setPresentationMode] = useState('full');
  const [restoreThreadId, setRestoreThreadId] = useState(() => loadDockState(teamId).threadId || null);
  const [entryPoint, setEntryPoint] = useState(() => loadDockState(teamId).entryPoint || null);
  const [orbHidden, setOrbHidden] = useState(!conductorWidgetEnabled);
  const [orbPosition, setOrbPosition] = useState(() => loadOrbState(teamId, currentUserId).position || null);
  const [viewportSize, setViewportSize] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight
  }));
  const [dockWidth, setDockWidth] = useState(() => loadDockState(teamId).width || DOCK_DEFAULT_WIDTH);
  const [isResizingDock, setIsResizingDock] = useState(false);
  const launchTourScheduledRef = useRef(false);
  const [voiceCall, setVoiceCall] = useState(null);
  const [voiceEndRequestId, setVoiceEndRequestId] = useState(0);
  const voiceCallStartedRef = useRef(false);

  const isQuickChat = isOpen && presentationMode === 'quick' && !isMobileLayout;
  const isDocked = isOpen && presentationMode === 'full' && !isMobileLayout;
  const isVoiceCall = isOpen && presentationMode === 'voice' && !isMobileLayout;
  const quickChatFrame = getQuickChatFrame(orbPosition, viewportSize.width, viewportSize.height);
  const effectiveDockWidth = clampDockWidth(dockWidth, viewportSize.width);
  const conductorInterface = isMobileLayout ? 'mobile' : isVoiceCall ? 'voice_call' : isQuickChat ? 'widget' : 'dock';

  const startDockResize = useCallback((event) => {
    event.preventDefault();
    setIsResizingDock(true);
    let latestWidth = effectiveDockWidth;

    const handleMove = (moveEvent) => {
      latestWidth = clampDockWidth(window.innerWidth - moveEvent.clientX, window.innerWidth);
      setDockWidth(latestWidth);
    };
    const handleEnd = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleEnd);
      window.removeEventListener('pointercancel', handleEnd);
      setIsResizingDock(false);
      saveDockState(teamId, { width: latestWidth });
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleEnd);
    window.addEventListener('pointercancel', handleEnd);
  }, [effectiveDockWidth, teamId]);

  const handleDockResizeKeyDown = useCallback((event) => {
    const step = { ArrowLeft: 16, ArrowRight: -16 }[event.key];
    if (!step) return;

    event.preventDefault();
    const nextWidth = clampDockWidth(effectiveDockWidth + step, window.innerWidth);
    setDockWidth(nextWidth);
    saveDockState(teamId, { width: nextWidth });
  }, [effectiveDockWidth, teamId]);

  const getCsrfToken = () => {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute('content') : '';
  };

  const handleClose = useCallback(() => {
    setIsOpen(false);
    saveDockState(teamId, { open: false });
  }, [teamId]);

  const maximizeToDock = useCallback(() => {
    setPresentationMode('full');
    saveDockState(teamId, { mode: 'docked', open: true });
  }, [teamId]);

  const minimizeToWidget = useCallback(() => {
    setPresentationMode('quick');
    saveDockState(teamId, { mode: 'quick', open: false });
  }, [teamId]);

  const rememberActiveThread = useCallback((threadId) => {
    saveDockState(teamId, { threadId: threadId || null });
  }, [teamId]);

  const saveOrbHidden = useCallback((hidden) => {
    setOrbHidden(hidden);
    client.patch(`/teams/${teamId}/team_memberships/${teamMembershipId}`, {
      team_membership: { conductor_widget_enabled: !hidden }
    });
  }, [teamId, teamMembershipId]);

  const toggleOrb = useCallback(() => saveOrbHidden(!orbHidden), [saveOrbHidden, orbHidden]);

  const dismissOrb = useCallback(() => {
    saveOrbHidden(true);
    window.dispatchEvent(new CustomEvent('conductorWidgetDismissed'));
  }, [saveOrbHidden]);

  const handleVoiceStateChange = useCallback((state) => {
    setVoiceCall(state);
    if (state.status !== 'idle') {
      voiceCallStartedRef.current = true;
      return;
    }
    if (!voiceCallStartedRef.current) return;

    voiceCallStartedRef.current = false;
    setPresentationMode((mode) => {
      if (mode === 'voice') setIsOpen(false);
      return mode;
    });
  }, []);

  const endVoiceCall = useCallback(() => {
    setVoiceEndRequestId((requestId) => requestId + 1);
  }, []);

  const startVoiceFromOrb = useCallback(() => {
    window.dispatchEvent(new CustomEvent('openConductor', { detail: { voice: true, source: 'orb' } }));
  }, []);

  const openFromOrb = useCallback((prompt, position) => {
    if (isQuickChat) {
      handleClose();
      return;
    }
    setOrbPosition(position);
    window.dispatchEvent(new CustomEvent('openConductor', {
      detail: {
        ...(prompt ? { prompt } : {}),
        presentation: isMobileLayout ? 'full' : 'quick',
        source: 'orb'
      }
    }));
  }, [handleClose, isMobileLayout, isQuickChat]);

  const closeLaunchTour = useCallback(() => {
    setIsLaunchTourVisible(false);
  }, []);

  const startFromLaunch = useCallback(() => {
    setIsLaunchTourVisible(false);
    window.dispatchEvent(new CustomEvent('openConductor', { detail: { source: 'launch_tour' } }));
  }, []);

  const promptFromLaunch = useCallback((prompt) => {
    setIsLaunchTourVisible(false);
    window.dispatchEvent(new CustomEvent('openConductor', { detail: { prompt, source: 'launch_tour' } }));
  }, []);

  useEffect(() => {
    const handleOpen = (event) => {
      const isMobile = getIsMobileLayout();
      const nextPresentation = event?.detail?.voice && !isMobile
        ? 'voice'
        : event?.detail?.presentation === 'quick' && !isMobile
          ? 'quick'
          : 'full';
      const storedDockState = loadDockState(teamId);
      const nextEntryPoint = event?.detail?.source || null;
      setPendingPrompt(event?.detail?.prompt || null);
      setEntryPoint(nextEntryPoint);
      saveDockState(teamId, { entryPoint: nextEntryPoint });
      setPendingVoice(Boolean(event?.detail?.voice));
      setRestoreThreadId(storedDockState.threadId || null);
      setPresentationMode(nextPresentation);
      setIsOpen(true);
      voiceCallStartedRef.current = false;
      setVoiceCall(null);
      if (nextPresentation === 'full') saveDockState(teamId, { mode: 'docked', open: true });
    };
    window.addEventListener('openConductor', handleOpen);
    return () => window.removeEventListener('openConductor', handleOpen);
  }, [teamId]);

  useEffect(() => {
    const stored = loadDockState(teamId);
    if (stored.open && stored.mode === 'docked' && !getIsMobileLayout()) {
      setRestoreThreadId(stored.threadId || null);
      setPresentationMode('full');
      setIsOpen(true);
    }
  }, [teamId]);

  useEffect(() => {
    if (!isDocked) return undefined;

    document.body.classList.add('conductor-dock-open');
    return () => document.body.classList.remove('conductor-dock-open');
  }, [isDocked]);

  // Pop the relaunch launch modal on app load (this modal is mounted globally in
  // the app layout), once per team — no need to open Conductor first.
  useEffect(() => {
    if (launchTourScheduledRef.current) {
      return undefined;
    }

    const key = teamId ? `venue_conductor_launch_seen_redesign_${teamId}` : 'venue_conductor_launch_seen_redesign';
    let hasSeen = false;
    try {
      hasSeen = localStorage.getItem(key) === 'seen';
    } catch (e) {
      hasSeen = true;
    }

    launchTourScheduledRef.current = true;

    if (hasSeen) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setIsLaunchTourVisible(true);
      // Mark seen only once it actually shows, so a fast reload can't suppress it.
      try {
        localStorage.setItem(key, 'seen');
      } catch (e) {
        // Local storage is optional.
      }
    }, 600);
    return () => window.clearTimeout(timer);
  }, [teamId]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileLayout(getIsMobileLayout());
      setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isOpen || isDocked || isVoiceCall) return undefined;

    const handleEsc = (e) => {
      if (e.key === 'Escape') handleClose();
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, isDocked, isVoiceCall, handleClose]);

  useEffect(() => {
    if (!isOpen || isDocked || isQuickChat || isVoiceCall) {
      return undefined;
    }

    const cleanupReadyCallback = hideHubSpotWidget();

    const { body, documentElement } = document;
    const hadThemedBody = body.classList.contains('od-themed');
    const originalBodyOverflow = body.style.overflow;
    const originalHtmlOverflow = documentElement.style.overflow;
    const originalBodyPosition = body.style.position;
    const originalBodyTop = body.style.top;
    const originalBodyWidth = body.style.width;
    const scrollY = window.scrollY;
    body.classList.add('od-themed');
    body.style.overflow = 'hidden';
    documentElement.style.overflow = 'hidden';
    if (isMobileLayout) {
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.width = '100%';
    }

    return () => {
      cleanupReadyCallback();
      restoreHubSpotWidget();
      body.style.overflow = originalBodyOverflow;
      documentElement.style.overflow = originalHtmlOverflow;
      body.style.position = originalBodyPosition;
      body.style.top = originalBodyTop;
      body.style.width = originalBodyWidth;
      if (!hadThemedBody) {
        body.classList.remove('od-themed');
      }
      if (isMobileLayout) {
        window.scrollTo(0, scrollY);
      }
    };
  }, [isOpen, isDocked, isQuickChat, isVoiceCall, isMobileLayout]);

  useEffect(() => {
    if (!isOpen || (!isDocked && !isQuickChat)) {
      return undefined;
    }

    const cleanupReadyCallback = hideHubSpotWidget();
    return () => {
      cleanupReadyCallback();
      restoreHubSpotWidget();
    };
  }, [isOpen, isDocked, isQuickChat]);

  useEffect(() => {
    if (!isOpen || !isMobileLayout) {
      setVisualViewportInsets(null);
      return undefined;
    }
    if (typeof window === 'undefined' || !window.visualViewport) {
      return undefined;
    }

    const viewport = window.visualViewport;
    const update = () => {
      setVisualViewportInsets({
        width: viewport.width,
        height: viewport.height,
        offsetTop: viewport.offsetTop,
        offsetLeft: viewport.offsetLeft
      });
    };

    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);

    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
    };
  }, [isOpen, isMobileLayout]);

  const mobileViewportFrame = isMobileLayout && visualViewportInsets ? {
    top: `${visualViewportInsets.offsetTop}px`,
    left: `${visualViewportInsets.offsetLeft}px`,
    width: `${visualViewportInsets.width}px`,
    height: `${visualViewportInsets.height}px`
  } : {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0
  };

  return (
    <>
      <style>{HUBSPOT_WIDGET_HIDDEN_STYLES}</style>
      <style>{dockBodyStyles(effectiveDockWidth, isResizingDock)}</style>
      {(!isOpen || isQuickChat || isVoiceCall) && !orbHidden && (
        <VenueConductorOrb
          teamId={teamId}
          currentUserId={currentUserId}
          onOpen={openFromOrb}
          onStartVoice={conductorVoiceEnabled ? startVoiceFromOrb : null}
          quickChatOpen={isQuickChat}
          voiceCall={isVoiceCall ? (voiceCall || { status: 'connecting' }) : null}
          onEndVoice={endVoiceCall}
          onPositionChange={setOrbPosition}
          onDismiss={dismissOrb}
        />
      )}
      <VenueConductorLaunchTour
        visible={isLaunchTourVisible}
        teamName={teamName}
        conductorVoiceEnabled={conductorVoiceEnabled}
        hasAiIntegration={Boolean(existingAiIntegration)}
        onClose={closeLaunchTour}
        onStart={startFromLaunch}
        onPromptSelect={promptFromLaunch}
      />
      {isOpen && (
        <div
          className={isQuickChat ? 'venue-conductor-quick-chat-frame' : undefined}
          data-testid={isQuickChat ? 'venue-conductor-quick-chat' : undefined}
          role={isQuickChat ? 'dialog' : undefined}
          aria-label={isQuickChat ? 'Conductor quick chat' : undefined}
          aria-hidden={isVoiceCall || undefined}
          style={isVoiceCall ? {
            position: 'fixed',
            top: 0,
            left: '-10000px',
            width: '456px',
            height: '600px',
            opacity: 0,
            pointerEvents: 'none'
          } : isQuickChat ? {
            top: `${quickChatFrame.top}px`,
            left: `${quickChatFrame.left}px`,
            width: `${quickChatFrame.width}px`,
            height: `${quickChatFrame.height}px`,
            '--conductor-frame-width': `${quickChatFrame.width}px`
          } : isDocked ? {
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: `${effectiveDockWidth}px`,
            '--conductor-frame-width': `${effectiveDockWidth}px`,
            zIndex: 1049,
            display: 'flex',
            overflow: 'visible'
          } : {
            position: 'fixed',
            ...mobileViewportFrame,
            zIndex: 1050,
            display: 'flex',
            alignItems: 'stretch',
            overflow: 'hidden'
          }}
        >
          {isDocked && (
            <div
              className={`venue-conductor-dock-resizer${isResizingDock ? ' is-resizing' : ''}`}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize Conductor"
              aria-valuenow={effectiveDockWidth}
              aria-valuemin={DOCK_MIN_WIDTH}
              aria-valuemax={clampDockWidth(Infinity, viewportSize.width)}
              tabIndex={0}
              onPointerDown={startDockResize}
              onKeyDown={handleDockResizeKeyDown}
            />
          )}
          <div
            className={`venue-conductor-modal-shell${isQuickChat ? ' venue-conductor-quick-chat-shell' : ''}${isDocked ? ' venue-conductor-dock-shell' : ''}`}
            data-placement={isQuickChat ? quickChatFrame.placement : undefined}
            style={isQuickChat ? undefined : isDocked ? {
            position: 'relative',
            flex: 1,
            minWidth: 0,
            height: '100%',
            backgroundColor: conductorTheme.surface,
            borderLeft: `1px solid ${conductorTheme.border}`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          } : {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: conductorTheme.surface,
            overflow: 'hidden',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column'
          }}>

            <div style={{ flex: 1, minHeight: 0 }}>
              <VenueConductorPage
                csrfToken={getCsrfToken()}
                containerHeight="100%"
                teamId={teamId}
                teamName={teamName}
                currentUserAvatarUrl={currentUserAvatarUrl}
                existingAiIntegration={existingAiIntegration}
                conductorVoiceEnabled={conductorVoiceEnabled}
                helpCenterEnabled={helpCenterEnabled}
                conductorTextingEnabled={conductorTextingEnabled}
                conductorCuesEnabled={conductorCuesEnabled}
                initialPrompt={pendingPrompt}
                autoStartVoice={pendingVoice}
                initialThreadId={restoreThreadId}
                onActiveThreadChange={rememberActiveThread}
                conductorInterface={conductorInterface}
                entryPoint={entryPoint}
                layoutMode={isQuickChat ? 'compact' : 'condensed'}
                renderToastContainer={false}
                onCloseConductor={handleClose}
                onExpandConductor={isQuickChat ? maximizeToDock : null}
                onMinimizeConductor={isDocked ? minimizeToWidget : null}
                onVoiceStateChange={handleVoiceStateChange}
                voiceEndRequestId={voiceEndRequestId}
                orbHidden={orbHidden}
                toggleOrb={toggleOrb}
              />
            </div>
          </div>
          <ToastContainer icon={false} />
        </div>
      )}
    </>
  );
};

VenueConductorModal.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  teamName: PropTypes.string,
  currentUserId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  teamMembershipId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  conductorWidgetEnabled: PropTypes.bool,
  currentUserAvatarUrl: PropTypes.string,
  existingAiIntegration: existingAiIntegrationPropType,
  conductorVoiceEnabled: PropTypes.bool,
  helpCenterEnabled: PropTypes.bool,
  conductorTextingEnabled: PropTypes.bool,
  conductorCuesEnabled: PropTypes.bool
};

export default VenueConductorModal;
